import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Button } from '@safetyhub/ui';
import { createPortal } from 'react-dom';

type Tool = 'pen' | 'highlight' | 'eraser' | 'arrow' | 'crop';
type StrokeColor = 'red' | 'blue' | 'black';
type StrokeWidth = 2 | 4;

interface DrawElement {
  tool: Tool;
  color: string;
  width: number;
  points: { x: number; y: number }[];
}

interface CropSnapshot {
  backgroundImage: HTMLImageElement;
  elements: DrawElement[];
  canvasSize: { width: number; height: number };
}

type HistoryEntry =
  | { type: 'draw'; element: DrawElement }
  | { type: 'crop'; before: CropSnapshot; after: CropSnapshot };

interface ScreenshotAnnotatorProps {
  screenshotBlob: Blob;
  onExport: (annotatedBlob: Blob) => void;
  onSkip: () => void;
}

const COLOR_MAP: Record<StrokeColor, string> = {
  red: '#ef4444',
  blue: '#3b82f6',
  black: '#111827',
};

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const MIN_CROP_SIZE = 20;

export default function ScreenshotAnnotator({
  screenshotBlob,
  onExport,
  onSkip,
}: ScreenshotAnnotatorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bgImageRef = useRef<HTMLImageElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState<StrokeColor>('red');
  const [strokeWidth, setStrokeWidth] = useState<StrokeWidth>(2);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [redoStack, setRedoStack] = useState<HistoryEntry[]>([]);

  // history에서 elements 파생
  const elements = useMemo(
    () => history.filter((h): h is Extract<HistoryEntry, { type: 'draw' }> => h.type === 'draw').map((h) => h.element),
    [history],
  );
  const isDrawingRef = useRef(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const currentElementRef = useRef<DrawElement | null>(null);

  // 크롭 상태
  const [cropRegion, setCropRegion] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const cropStartRef = useRef<{ x: number; y: number } | null>(null);
  const isCroppingRef = useRef(false);
  const prevToolRef = useRef<Tool>('pen');

  // 줌 상태: scale + 오프셋 (캔버스 중심 기준)
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);

  // 패닝 상태
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panRef = useRef({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const panStartMouseRef = useRef({ x: 0, y: 0 });
  const spaceHeldRef = useRef(false);
  // cursor는 ref 값 변화를 반영하기 위해 상태로 관리
  const [panCursor, setPanCursor] = useState<'crosshair' | 'grab' | 'grabbing'>('crosshair');

  // 배경 스크롤 방지
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // ESC 키로 건너뛰기, Space 키로 패닝 모드 활성화
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onSkip();
      if (e.key === ' ' && !e.repeat) {
        e.preventDefault();
        spaceHeldRef.current = true;
        setPanCursor('grab');
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === ' ') {
        spaceHeldRef.current = false;
        isPanningRef.current = false;
        setPanCursor('crosshair');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [onSkip]);

  useEffect(() => {
    const url = URL.createObjectURL(screenshotBlob);
    const prevUrl = blobUrlRef.current;
    blobUrlRef.current = url;

    const img = new Image();
    img.onload = () => {
      if (blobUrlRef.current !== url) return;
      bgImageRef.current = img;
      const container = containerRef.current;
      if (!container) return;
      const maxW = container.clientWidth - 32;
      const maxH = container.clientHeight - 168;
      const ratio = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1);
      setCanvasSize({
        width: Math.floor(img.naturalWidth * ratio),
        height: Math.floor(img.naturalHeight * ratio),
      });
    };
    img.onerror = () => {
      if (blobUrlRef.current !== url) return;
      bgImageRef.current = null;
      const container = containerRef.current;
      if (!container) return;
      const maxW = container.clientWidth - 32;
      const maxH = container.clientHeight - 168;
      setCanvasSize({ width: Math.floor(maxW), height: Math.floor(maxH) });
    };
    img.src = url;

    if (prevUrl) {
      setTimeout(() => URL.revokeObjectURL(prevUrl), 1000);
    }
  }, [screenshotBlob]);

  // 컴포넌트 언마운트 시 최종 cleanup
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  // 캔버스 다시 그리기
  const redraw = useCallback(
    (elems: DrawElement[], cropOverlay?: { x: number; y: number; width: number; height: number } | null) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const img = bgImageRef.current;
      if (img) {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      }

      elems.forEach((el) => {
        if (!el || !el.points || el.points.length < 1) return;
        if (el.tool === 'eraser') return;
        ctx.save();
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        if (el.tool === 'highlight') {
          ctx.globalAlpha = 0.35;
          ctx.strokeStyle = '#facc15';
          ctx.lineWidth = el.width * 8;
        } else if (el.tool === 'arrow') {
          ctx.globalCompositeOperation = 'source-over';
          ctx.strokeStyle = el.color;
          ctx.lineWidth = el.width;
        } else {
          ctx.globalCompositeOperation = 'source-over';
          ctx.strokeStyle = el.color;
          ctx.lineWidth = el.width;
        }

        if (el.tool === 'arrow' && el.points.length >= 2) {
          const start = el.points[0];
          const end = el.points[el.points.length - 1];
          drawArrow(ctx, start.x, start.y, end.x, end.y, el.width);
        } else {
          ctx.beginPath();
          ctx.moveTo(el.points[0].x, el.points[0].y);
          el.points.forEach((p) => ctx.lineTo(p.x, p.y));
          ctx.stroke();
        }
        ctx.restore();
      });

      // 크롭 오버레이 렌더링
      const region = cropOverlay !== undefined ? cropOverlay : cropRegion;
      if (region && region.width > 0 && region.height > 0) {
        ctx.save();
        // 선택 영역 바깥쪽만 4개 사각형으로 어둡게 처리 (선택 영역 내부는 원본 그대로)
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        // 위쪽
        ctx.fillRect(0, 0, canvas.width, region.y);
        // 아래쪽
        ctx.fillRect(0, region.y + region.height, canvas.width, canvas.height - region.y - region.height);
        // 왼쪽
        ctx.fillRect(0, region.y, region.x, region.height);
        // 오른쪽
        ctx.fillRect(region.x + region.width, region.y, canvas.width - region.x - region.width, region.height);
        ctx.restore();

        // 선택 영역 테두리 (점선)
        ctx.save();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 4]);
        ctx.strokeRect(region.x, region.y, region.width, region.height);
        ctx.restore();

        // 크기 표시 텍스트
        const sizeLabel = `${Math.round(region.width)} × ${Math.round(region.height)}`;
        ctx.save();
        ctx.font = '12px sans-serif';
        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        const textX = region.x + region.width / 2;
        const textY = region.y > 20 ? region.y - 6 : region.y + region.height + 16;
        const textWidth = ctx.measureText(sizeLabel).width + 8;
        ctx.fillRect(textX - textWidth / 2, textY - 13, textWidth, 16);
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.fillText(sizeLabel, textX, textY - 1);
        ctx.restore();
      }
    },
    [cropRegion],
  );

  useEffect(() => {
    if (canvasSize.width > 0) {
      redraw(elements);
    }
  }, [canvasSize, redraw, elements, cropRegion]);

  // 패닝 범위 클램핑: 캔버스가 컨테이너 밖으로 너무 벗어나지 않도록 제한
  const clampPan = useCallback(
    (x: number, y: number, currentZoom: number) => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return { x, y };
      // 확대된 캔버스 크기
      const scaledW = canvasSize.width * currentZoom;
      const scaledH = canvasSize.height * currentZoom;
      // 컨테이너 크기
      const contW = container.clientWidth;
      const contH = container.clientHeight;
      // 확대분만큼만 이동 허용 (캔버스가 컨테이너 밖으로 절반 이상 넘어가지 않도록)
      const maxX = Math.max(0, (scaledW - contW) / 2 / currentZoom);
      const maxY = Math.max(0, (scaledH - contH) / 2 / currentZoom);
      return {
        x: Math.min(maxX, Math.max(-maxX, x)),
        y: Math.min(maxY, Math.max(-maxY, y)),
      };
    },
    [canvasSize],
  );

  // 마우스 휠 줌 처리 — wheel 이벤트는 passive:false로 등록해야 preventDefault 가능
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // 크롭 모드에서는 줌 비활성화
      if (tool === 'crop') return;

      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoomRef.current * delta));
      zoomRef.current = newZoom;
      setZoom(newZoom);
      // 줌이 1이 되면 pan + cursor 초기화
      if (newZoom <= 1) {
        panRef.current = { x: 0, y: 0 };
        setPan({ x: 0, y: 0 });
        isPanningRef.current = false;
        setPanCursor('crosshair');
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [tool]);

  function drawArrow(
    ctx: CanvasRenderingContext2D,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    lineWidth: number,
  ) {
    const headLen = Math.max(10, lineWidth * 4);
    const angle = Math.atan2(y2 - y1, x2 - x1);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(
      x2 - headLen * Math.cos(angle - Math.PI / 6),
      y2 - headLen * Math.sin(angle - Math.PI / 6),
    );
    ctx.moveTo(x2, y2);
    ctx.lineTo(
      x2 - headLen * Math.cos(angle + Math.PI / 6),
      y2 - headLen * Math.sin(angle + Math.PI / 6),
    );
    ctx.stroke();
  }

  // zoom을 고려한 캔버스 내 좌표 변환
  function getPos(e: React.MouseEvent | React.TouchEvent): { x: number; y: number } | null {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    let clientX: number;
    let clientY: number;
    if ('touches' in e) {
      const touch = e.touches[0] || e.changedTouches[0];
      clientX = touch.clientX;
      clientY = touch.clientY;
    } else {
      clientX = (e as React.MouseEvent).clientX;
      clientY = (e as React.MouseEvent).clientY;
    }
    // getBoundingClientRect는 이미 CSS transform(scale)이 적용된 화면 좌표를 반환
    // 캔버스 내부 픽셀 좌표로 변환: (화면 좌표 - rect) / zoom * (canvas 내부 크기 / rect 크기)
    // rect.width = canvasSize.width * zoom, rect.height = canvasSize.height * zoom
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  }

  // eraser: 포인터 위치 근처의 스트로크 제거
  const eraseAtPoint = useCallback(
    (pos: { x: number; y: number }) => {
      const ERASER_RADIUS = 12;
      setHistory((prev) => {
        const filtered = prev.filter((entry) => {
          if (entry.type !== 'draw') return true;
          const el = entry.element;
          if (el.tool === 'eraser') return false;
          return !el.points.some(
            (p) => Math.abs(p.x - pos.x) < ERASER_RADIUS && Math.abs(p.y - pos.y) < ERASER_RADIUS,
          );
        });
        if (filtered.length !== prev.length) {
          setRedoStack([]);
        }
        return filtered;
      });
    },
    [],
  );

  const getClientXY = (e: MouseEvent | React.MouseEvent | React.TouchEvent | TouchEvent): { x: number; y: number } => {
    if ('touches' in e) {
      const touch = (e as TouchEvent | React.TouchEvent).touches[0] || (e as TouchEvent | React.TouchEvent).changedTouches[0];
      return { x: touch.clientX, y: touch.clientY };
    }
    return { x: (e as MouseEvent | React.MouseEvent).clientX, y: (e as MouseEvent | React.MouseEvent).clientY };
  };

  // 패닝/크롭 중 document 레벨 mousemove/mouseup 처리 (canvas 밖으로 나가도 유지)
  useEffect(() => {
    const onDocMouseMove = (e: MouseEvent) => {
      if (isPanningRef.current) {
        const { x, y } = getClientXY(e);
        const dx = (x - panStartMouseRef.current.x) / zoomRef.current;
        const dy = (y - panStartMouseRef.current.y) / zoomRef.current;
        const raw = {
          x: panStartRef.current.x + dx,
          y: panStartRef.current.y + dy,
        };
        const clamped = clampPan(raw.x, raw.y, zoomRef.current);
        panRef.current = clamped;
        setPan({ ...clamped });
        return;
      }

      if (isCroppingRef.current && cropStartRef.current) {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const posX = (e.clientX - rect.left) * scaleX;
        const posY = (e.clientY - rect.top) * scaleY;
        const x = Math.min(cropStartRef.current.x, posX);
        const y = Math.min(cropStartRef.current.y, posY);
        const width = Math.abs(posX - cropStartRef.current.x);
        const height = Math.abs(posY - cropStartRef.current.y);
        const clampedX = Math.max(0, x);
        const clampedY = Math.max(0, y);
        const clampedW = Math.min(width, canvas.width - clampedX);
        const clampedH = Math.min(height, canvas.height - clampedY);
        // setCropRegion 대신 직접 redraw로 실시간 오버레이 표시 (상태 업데이트 없이)
        const region = { x: clampedX, y: clampedY, width: clampedW, height: clampedH };
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        // elements 상태는 클로저로 캡처되지 않으므로 별도 ref 필요 — 여기선 canvas 재draw
        // redraw 함수 직접 호출 (elements는 최신 값 필요)
        setCropRegion(region);
      }
    };

    const onDocMouseUp = (e: MouseEvent) => {
      if (isPanningRef.current) {
        isPanningRef.current = false;
        setPanCursor(spaceHeldRef.current ? 'grab' : 'crosshair');
        return;
      }

      if (isCroppingRef.current) {
        // handlePointerUp과 동일 로직 수행
        isCroppingRef.current = false;
        const canvas = canvasRef.current;
        if (!canvas || !cropStartRef.current) {
          cropStartRef.current = null;
          return;
        }
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const posX = (e.clientX - rect.left) * scaleX;
        const posY = (e.clientY - rect.top) * scaleY;
        const x = Math.min(cropStartRef.current.x, posX);
        const y = Math.min(cropStartRef.current.y, posY);
        const width = Math.abs(posX - cropStartRef.current.x);
        const height = Math.abs(posY - cropStartRef.current.y);
        cropStartRef.current = null;

        if (width < MIN_CROP_SIZE || height < MIN_CROP_SIZE) {
          setCropRegion(null);
          return;
        }
        const clampedX = Math.max(0, x);
        const clampedY = Math.max(0, y);
        const clampedW = Math.min(width, canvas.width - clampedX);
        const clampedH = Math.min(height, canvas.height - clampedY);
        setCropRegion({ x: clampedX, y: clampedY, width: clampedW, height: clampedH });
      }
    };

    document.addEventListener('mousemove', onDocMouseMove);
    document.addEventListener('mouseup', onDocMouseUp);
    return () => {
      document.removeEventListener('mousemove', onDocMouseMove);
      document.removeEventListener('mouseup', onDocMouseUp);
    };
  }, [clampPan]);

  const handlePointerDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      // 크롭 모드에서는 패닝 불가
      if (tool !== 'crop') {
        // 중클릭(button=1) 또는 Space+좌클릭이면 패닝 시작
        if ('button' in e) {
          const me = e as React.MouseEvent;
          const isPanTrigger = me.button === 1 || (me.button === 0 && spaceHeldRef.current);
          if (isPanTrigger) {
            e.preventDefault();
            const { x, y } = getClientXY(e);
            isPanningRef.current = true;
            panStartMouseRef.current = { x, y };
            panStartRef.current = { ...panRef.current };
            setPanCursor('grabbing');
            return;
          }
        }
      }

      const pos = getPos(e);
      if (!pos) return;

      if (tool === 'crop') {
        isCroppingRef.current = true;
        cropStartRef.current = pos;
        setCropRegion(null);
        return;
      }

      if (tool === 'eraser') {
        isDrawingRef.current = true;
        eraseAtPoint(pos);
        return;
      }

      const newEl: DrawElement = {
        tool,
        color: COLOR_MAP[color],
        width: strokeWidth,
        points: [pos],
      };
      currentElementRef.current = newEl;
      isDrawingRef.current = true;
      setRedoStack([]);

    },
    [tool, color, strokeWidth, eraseAtPoint],
  );

  const handlePointerMove = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      // 패닝은 document 레벨에서 처리하므로 여기선 그리기만
      if (isPanningRef.current) return;

      if (tool === 'crop' && isCroppingRef.current && cropStartRef.current) {
        const pos = getPos(e);
        if (!pos) return;
        const x = Math.min(cropStartRef.current.x, pos.x);
        const y = Math.min(cropStartRef.current.y, pos.y);
        const width = Math.abs(pos.x - cropStartRef.current.x);
        const height = Math.abs(pos.y - cropStartRef.current.y);
        const canvas = canvasRef.current;
        // 캔버스 범위 클램핑
        const clampedX = Math.max(0, x);
        const clampedY = Math.max(0, y);
        const clampedW = canvas ? Math.min(width, canvas.width - clampedX) : width;
        const clampedH = canvas ? Math.min(height, canvas.height - clampedY) : height;
        const region = { x: clampedX, y: clampedY, width: clampedW, height: clampedH };
        redraw(elements, region);
        return;
      }

      if (!isDrawingRef.current) return;
      const pos = getPos(e);
      if (!pos) return;

      if (tool === 'eraser') {
        eraseAtPoint(pos);
        return;
      }

      if (!currentElementRef.current) return;
      currentElementRef.current.points.push(pos);
      redraw([...elements, currentElementRef.current]);
    },
    [tool, elements, redraw, eraseAtPoint],
  );

  const handlePointerUp = useCallback(
    (e?: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent) => {
      // 패닝 종료는 document mouseup에서 처리
      if (isPanningRef.current) return;

      if (tool === 'crop' && isCroppingRef.current && cropStartRef.current) {
        isCroppingRef.current = false;
        const canvas = canvasRef.current;
        if (!canvas || !e) {
          cropStartRef.current = null;
          return;
        }
        // 마지막 pos 계산
        let clientX = 0;
        let clientY = 0;
        if ('touches' in e) {
          const touch = (e as TouchEvent | React.TouchEvent).changedTouches[0];
          clientX = touch.clientX;
          clientY = touch.clientY;
        } else {
          clientX = (e as MouseEvent | React.MouseEvent).clientX;
          clientY = (e as MouseEvent | React.MouseEvent).clientY;
        }
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const posX = (clientX - rect.left) * scaleX;
        const posY = (clientY - rect.top) * scaleY;

        const x = Math.min(cropStartRef.current.x, posX);
        const y = Math.min(cropStartRef.current.y, posY);
        const width = Math.abs(posX - cropStartRef.current.x);
        const height = Math.abs(posY - cropStartRef.current.y);
        cropStartRef.current = null;

        // 최소 크기 미만이면 무시
        if (width < MIN_CROP_SIZE || height < MIN_CROP_SIZE) {
          redraw(elements, null);
          return;
        }

        const clampedX = Math.max(0, x);
        const clampedY = Math.max(0, y);
        const clampedW = Math.min(width, canvas.width - clampedX);
        const clampedH = Math.min(height, canvas.height - clampedY);
        setCropRegion({ x: clampedX, y: clampedY, width: clampedW, height: clampedH });
        return;
      }

      if (!isDrawingRef.current) return;
      isDrawingRef.current = false;

      if (tool === 'eraser') {
        return;
      }

      const finished = currentElementRef.current;
      currentElementRef.current = null;
      if (finished && finished.points.length > 0) {
        setHistory((prev) => [...prev, { type: 'draw', element: finished }]);
      }
    },
    [tool, elements, redraw],
  );

  // canvas mouseLeave: 패닝 중이면 무시 (document에서 계속 추적), 크롭 중이면 계속 진행
  const handleMouseLeave = useCallback(() => {
    if (isPanningRef.current) return;
    if (isCroppingRef.current) return;
    handlePointerUp();
  }, [handlePointerUp]);

  const undo = () => {
    if (history.length === 0) return;
    const last = history[history.length - 1];
    setRedoStack((prev) => [...prev, last]);
    if (last.type === 'draw') {
      // 그리기 undo: history에서 마지막 draw 항목 제거
      setHistory((prev) => prev.slice(0, -1));
    } else {
      // 크롭 undo: before 스냅샷으로 배경/elements/캔버스 크기 복원
      const { before } = last;
      bgImageRef.current = before.backgroundImage;
      setCanvasSize(before.canvasSize);
      zoomRef.current = 1;
      setZoom(1);
      panRef.current = { x: 0, y: 0 };
      setPan({ x: 0, y: 0 });
      // crop entry를 제거하고 before.elements를 draw 항목으로 복원
      setHistory(before.elements.map((el) => ({ type: 'draw' as const, element: el })));
    }
  };

  const redo = () => {
    if (redoStack.length === 0) return;
    const last = redoStack[redoStack.length - 1];
    setRedoStack((prev) => prev.slice(0, -1));
    if (last.type === 'draw') {
      setHistory((prev) => [...prev, last]);
    } else {
      // 크롭 redo: after 스냅샷으로 배경/elements/캔버스 크기 복원
      const { after } = last;
      bgImageRef.current = after.backgroundImage;
      setCanvasSize(after.canvasSize);
      zoomRef.current = 1;
      setZoom(1);
      panRef.current = { x: 0, y: 0 };
      setPan({ x: 0, y: 0 });
      // after.elements를 draw 항목으로 복원하고 crop entry 추가
      setHistory([
        ...after.elements.map((el) => ({ type: 'draw' as const, element: el })),
        last,
      ]);
    }
  };

  const resetZoom = () => {
    zoomRef.current = 1;
    setZoom(1);
    panRef.current = { x: 0, y: 0 };
    setPan({ x: 0, y: 0 });
  };

  const cancelCrop = () => {
    setCropRegion(null);
    isCroppingRef.current = false;
    cropStartRef.current = null;
    setTool(prevToolRef.current);
  };

  const applyCrop = () => {
    if (!cropRegion) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    // 크롭 전 상태 스냅샷
    const beforeSnapshot: CropSnapshot = {
      backgroundImage: bgImageRef.current!,
      elements: elements,
      canvasSize: { width: canvasSize.width, height: canvasSize.height },
    };

    const { x, y, width, height } = cropRegion;

    // 1. 임시 canvas에 현재 canvas의 선택 영역 그리기 (오버레이 없이)
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return;

    // 현재 그리기 요소를 오버레이 없이 렌더링한 클린 캔버스에서 크롭
    const cleanCanvas = document.createElement('canvas');
    cleanCanvas.width = canvas.width;
    cleanCanvas.height = canvas.height;
    const cleanCtx = cleanCanvas.getContext('2d');
    if (!cleanCtx) return;

    // 배경 이미지
    const img = bgImageRef.current;
    if (img) {
      cleanCtx.drawImage(img, 0, 0, canvas.width, canvas.height);
    }
    // elements 렌더링 (오버레이 없이)
    elements.forEach((el) => {
      if (!el || !el.points || el.points.length < 1) return;
      if (el.tool === 'eraser') return;
      cleanCtx.save();
      cleanCtx.lineCap = 'round';
      cleanCtx.lineJoin = 'round';
      if (el.tool === 'highlight') {
        cleanCtx.globalAlpha = 0.35;
        cleanCtx.strokeStyle = '#facc15';
        cleanCtx.lineWidth = el.width * 8;
      } else if (el.tool === 'arrow') {
        cleanCtx.globalCompositeOperation = 'source-over';
        cleanCtx.strokeStyle = el.color;
        cleanCtx.lineWidth = el.width;
      } else {
        cleanCtx.globalCompositeOperation = 'source-over';
        cleanCtx.strokeStyle = el.color;
        cleanCtx.lineWidth = el.width;
      }
      if (el.tool === 'arrow' && el.points.length >= 2) {
        const start = el.points[0];
        const end = el.points[el.points.length - 1];
        drawArrow(cleanCtx, start.x, start.y, end.x, end.y, el.width);
      } else {
        cleanCtx.beginPath();
        cleanCtx.moveTo(el.points[0].x, el.points[0].y);
        el.points.forEach((p) => cleanCtx.lineTo(p.x, p.y));
        cleanCtx.stroke();
      }
      cleanCtx.restore();
    });

    // 크롭 영역만 tempCanvas에 그리기
    tempCtx.drawImage(cleanCanvas, x, y, width, height, 0, 0, width, height);

    // 2. 크롭된 이미지를 새 배경으로 설정
    const croppedImg = new Image();
    croppedImg.onload = () => {
      bgImageRef.current = croppedImg;

      // 3. elements 좌표 offset 적용
      const shiftedElements = elements.map((el) => ({
        ...el,
        points: el.points.map((p) => ({ x: p.x - x, y: p.y - y })),
      }));

      // 크롭 후 상태 스냅샷
      const afterSnapshot: CropSnapshot = {
        backgroundImage: croppedImg,
        elements: shiftedElements,
        canvasSize: { width, height },
      };

      // 통합 히스토리에 crop entry 추가 (이전 draw 항목 모두 교체)
      // 크롭 후에는 draw 항목을 shiftedElements 기반으로 재구성
      const cropEntry: HistoryEntry = { type: 'crop', before: beforeSnapshot, after: afterSnapshot };
      const newHistory: HistoryEntry[] = [
        ...shiftedElements.map((el): HistoryEntry => ({ type: 'draw', element: el })),
        cropEntry,
      ];
      setHistory(newHistory);
      setRedoStack([]);

      // 4. canvas 크기 변경
      setCanvasSize({ width, height });

      // 5. cropRegion 해제, zoom/pan 리셋, 이전 도구로 복귀
      setCropRegion(null);
      isCroppingRef.current = false;
      cropStartRef.current = null;
      zoomRef.current = 1;
      setZoom(1);
      panRef.current = { x: 0, y: 0 };
      setPan({ x: 0, y: 0 });
      setTool(prevToolRef.current);
    };
    croppedImg.src = tempCanvas.toDataURL('image/png');
  };

  const handleExport = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob(
      (blob) => {
        if (blob) onExport(blob);
      },
      'image/jpeg',
      0.85,
    );
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex flex-col bg-black/80"
      role="dialog"
      aria-modal="true"
      aria-label="스크린샷 메모"
    >
      {/* 상단 툴바 — 어두운 에디터 크롬(ink-900) 위의 도구 선택·undo/redo.
          @safetyhub/ui Button은 밝은 바탕 전제(secondary=흰 박스)라 이 표면엔 맞지 않는다.
          이 영역은 에디터 자체 디자인이 정본이므로 프리미티브로 이관하지 않는다. */}
      {/* eslint-disable local/no-self-painted-button */}
      <div className="flex flex-wrap items-center gap-2 bg-ink-900 border-b border-ink-700 px-4 py-3 shrink-0">
        <span className="text-white text-sm font-medium mr-2 hidden sm:inline">스크린샷 메모</span>

        {/* 도구 선택 */}
        <div className="flex gap-1">
          {(
            [
              { id: 'pen', label: '펜' },
              { id: 'highlight', label: '형광펜' },
              { id: 'arrow', label: '화살표' },
              { id: 'eraser', label: '지우개' },
              { id: 'crop', label: '크롭' },
            ] as { id: Tool; label: string }[]
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                if (t.id !== 'crop') {
                  prevToolRef.current = t.id;
                  // 크롭 모드 해제 시 크롭 영역 초기화
                  if (tool === 'crop') {
                    setCropRegion(null);
                    isCroppingRef.current = false;
                    cropStartRef.current = null;
                  }
                } else {
                  // 크롭 도구 선택 시 현재 도구를 prevTool로 저장
                  if (tool !== 'crop') {
                    prevToolRef.current = tool;
                  }
                }
                setTool(t.id);
              }}
              className={`px-2 py-1 text-xs rounded font-medium transition-colors ${
                tool === t.id
                  ? t.id === 'crop'
                    ? 'bg-amber-500 text-white'
                    : 'bg-brand-500 text-white'
                  : 'bg-ink-700 border border-ink-600 text-ink-200 hover:bg-ink-600'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* 크롭 모드 안내 */}
        {tool === 'crop' && (
          <span className="text-xs text-amber-300 hidden sm:inline">드래그하여 영역 선택</span>
        )}

        {/* 색상 선택 (펜/화살표만 해당) */}
        {(tool === 'pen' || tool === 'arrow') && (
          <div className="flex gap-1.5 items-center">
            {(Object.entries(COLOR_MAP) as [StrokeColor, string][]).map(([key, val]) => (
              <button
                key={key}
                type="button"
                onClick={() => setColor(key)}
                className={`w-5 h-5 rounded-full border-2 transition-transform ${
                  color === key ? 'border-white scale-125' : 'border-ink-500'
                }`}
                style={{ backgroundColor: val }}
                aria-label={key}
              />
            ))}
          </div>
        )}

        {/* 굵기 */}
        {tool !== 'eraser' && tool !== 'highlight' && (
          <div className="flex gap-1 items-center">
            {([2, 4] as StrokeWidth[]).map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => setStrokeWidth(w)}
                className={`w-6 h-6 flex items-center justify-center rounded border transition-colors ${
                  strokeWidth === w ? 'border-brand-400 bg-brand-900' : 'border-ink-600 bg-ink-700'
                }`}
              >
                <div
                  className="rounded-full bg-ink-200"
                  style={{ width: w * 2, height: w * 2 }}
                />
              </button>
            ))}
          </div>
        )}

        {/* Undo / Redo */}
        <div className="flex gap-1 ml-auto">
          <button
            type="button"
            onClick={undo}
            disabled={history.length === 0}
            className="px-2 py-1 text-xs rounded border border-ink-600 bg-ink-700 text-ink-200 hover:bg-ink-600 disabled:opacity-40"
          >
            되돌리기
          </button>
          <button
            type="button"
            onClick={redo}
            disabled={redoStack.length === 0}
            className="px-2 py-1 text-xs rounded border border-ink-600 bg-ink-700 text-ink-200 hover:bg-ink-600 disabled:opacity-40"
          >
            다시하기
          </button>
        </div>
      </div>
      {/* eslint-enable local/no-self-painted-button */}

      {/* 중앙 캔버스 영역 */}
      <div
        ref={containerRef}
        className="relative flex-1 flex justify-center items-center overflow-hidden p-4"
      >
        {canvasSize.width > 0 && (
          <canvas
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            className="touch-none shadow-2xl"
            style={{
              transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
              transformOrigin: 'center center',
              cursor: tool === 'crop' ? 'crosshair' : panCursor,
            }}
            onMouseDown={handlePointerDown}
            onMouseMove={handlePointerMove}
            onMouseUp={handlePointerUp}
            onMouseLeave={handleMouseLeave}
            onAuxClick={(e) => e.preventDefault()}
            onContextMenu={(e) => e.preventDefault()}
            onTouchStart={handlePointerDown}
            onTouchMove={handlePointerMove}
            onTouchEnd={handlePointerUp}
            onTouchCancel={handlePointerUp}
          />
        )}
        {/* 크롭 확인/취소 버튼 — 캔버스 위 오버레이(어두운 크롬)라 위 툴바와 같은 이유로 유지. */}
        {/* eslint-disable local/no-self-painted-button */}
        {tool === 'crop' && cropRegion && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            <button
              type="button"
              onClick={applyCrop}
              className="px-3 py-1.5 text-xs font-medium rounded bg-amber-500 text-white hover:bg-amber-600 shadow-lg"
            >
              이 영역만 사용
            </button>
            <button
              type="button"
              onClick={cancelCrop}
              className="px-3 py-1.5 text-xs font-medium rounded bg-ink-700 text-ink-200 border border-ink-600 hover:bg-ink-600 shadow-lg"
            >
              취소
            </button>
          </div>
        )}
        {/* eslint-enable local/no-self-painted-button */}

        {/* 줌 인디케이터 + 리셋 버튼 — 캔버스 transform 영향 밖에 배치 */}
        {zoom !== 1 && tool !== 'crop' && (
          <button
            type="button"
            onClick={resetZoom}
            className="absolute bottom-4 right-4 px-2 py-1 text-xs rounded bg-black/60 text-white border border-ink-600 hover:bg-black/80"
          >
            {Math.round(zoom * 100)}% 리셋
          </button>
        )}
      </div>

      {/* 하단 액션 버튼 */}
      <div className="flex justify-between items-center bg-ink-900 border-t border-ink-700 px-4 py-3 shrink-0">
        <p className="text-xs text-ink-400 hidden sm:block">ESC 건너뛰기 · 휠 확대/축소 · 확대 시 가운데클릭 또는 Space+드래그로 이동</p>
        <div className="flex gap-2 ml-auto">
          {/* 어두운 바탕이라 secondary의 흰 박스 대신 slate 계열로 덮어쓴다(치수·포커스 링은 정본). */}
          <Button
            variant="secondary"
            onClick={onSkip}
            className="border-ink-600 bg-transparent text-ink-200 hover:bg-ink-700 hover:text-white"
          >
            건너뛰기
          </Button>
          <Button variant="primary" onClick={handleExport}>
            메모 완료
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
